import { i18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { notification } from 'antd';
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { config } from 'config/environmentConfig';
import { getUrls } from './getUrls';

const { BASE_URL, BILLING_URL } = getUrls();
const TG_URL = config.TG_API;

interface ErrorResponseData {
  data?: { message?: string };
}

export class HTTPError extends Error {
  constructor(response: AxiosResponse<ErrorResponseData>) {
    super(response?.data?.data?.message);
  }
}

export const NOT_ALLOWED_IN_DEMO = {
  message: msg`Данные не доступны в демо-режиме`,
  code: 555
};

const handleResponse = (response: AxiosResponse<unknown>) => {
  if (response?.status === 204) {
    return {};
  }
  if (
    response?.data &&
    typeof response.data === 'object' &&
    'code' in response.data &&
    (response.data.code as number) < -1
  ) {
    throw new HTTPError(response as AxiosResponse<ErrorResponseData>);
  }
  let { data } = response || { data: undefined };
  if (typeof data === 'string' && data === 'undefined') return undefined;
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch (e) {
      data = { data };
    }
  }
  // Пустой JSON-результат не может содержать служебный Symbol Axios.
  if (data == null || typeof data === 'boolean') return data;
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  data[Symbol.for('axios')] = Object.keys(response).reduce(
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    (acc, key) => (key === 'data' ? acc : { ...acc, [key]: response[key] }),
    {}
  );
  return data;
};
/** @deprecated add service to src/services  */
export const SERVICES = {
  default: axios.create({
    baseURL: BASE_URL
  }),
  billing: axios.create({
    baseURL: BILLING_URL
  }),
  TG: axios.create({
    baseURL: TG_URL
  }),
  external: axios.create({
    headers: undefined
  })
};

export function getAxios(url: string) {
  switch (true) {
    case /billing/.test(url):
      return SERVICES.billing;
    case /^https/.test(url):
      return SERVICES.external;
    default:
      return SERVICES.default;
  }
}

/**
 * @deprecated
 * Используйте методы из reduxjs/toolkit (папка services)
 */
export async function post<R, D, P>(
  url: string,
  data: D,
  params?: P
): Promise<R> {
  const response = await getAxios(url)({
    url,
    method: 'POST',
    data,
    ...params
  });
  return handleResponse(response) as Promise<R>;
}

/**
 * @deprecated
 * Используйте методы из reduxjs/toolkit (папка services)
 */
export async function put<R, D, P>(
  url: string,
  data: D,
  params?: P
): Promise<R> {
  const response = await getAxios(url)({ url, method: 'PUT', data, ...params });
  return handleResponse(response) as Promise<R>;
}

/**
 * @deprecated
 * Используйте методы из reduxjs/toolkit (папка services)
 */
export async function get<R, P = unknown>(
  url: string,
  params?: P,
  requestConfig: AxiosRequestConfig = {}
): Promise<R> {
  const response = await getAxios(url)({
    url,
    method: 'GET',
    params,
    ...requestConfig
  });
  return handleResponse(response) as Promise<R>;
}

/**
 * @deprecated
 * Используйте методы из @reduxjs/toolkit (папка services)
 */
export function httpDelete<P>(url: string, params?: P) {
  return getAxios(url)({ url, method: 'DELETE', params });
}
export type RequestConfig = AxiosRequestConfig & { url: string };
export async function request<R>(requestConfig: RequestConfig): Promise<R> {
  const serviceInstance = getAxios(requestConfig.url);
  try {
    const response = await serviceInstance(requestConfig);
    return await (handleResponse(response) as Promise<R>);
  } catch (e) {
    if (
      typeof e === 'object' &&
      e &&
      'response' in e &&
      typeof e.response === 'object' &&
      e.response &&
      'status' in e.response &&
      e.response?.status === NOT_ALLOWED_IN_DEMO.code
    ) {
      notification.warning({
        message: i18n.t(NOT_ALLOWED_IN_DEMO.message),
        key: 'NOT_ALLOWED_IN_DEMO'
      });
    }
    throw e;
  }
}

export function buildFilterParams(f: Record<string, unknown>) {
  const filterKeys = Object.keys(f);
  if (filterKeys.length === 0) {
    return {
      filter: undefined
    };
  }
  return {
    filter: filterKeys.reduce(
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      (acc, k) => (f[k] ? [...acc, `${k}^="${f[k]}"`] : acc),
      [] as string[]
    )[0]
  };
}
